import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import API_BASE from "./config";
import Pagination from "./components/Pagination";
import SearchableSelect from "./components/SearchableSelect";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { usePermissions, RequirePermission } from "./hooks/usePermissions";
import "./css/UserData.css";
import { useNavigate } from "react-router-dom";

const MAX_SINGLE_UPLOAD_SIZE = 5 * 1024 * 1024;

const UserData = () => {
  // Cognito user id (sub) — what /user-files expects
  const [cognitoUserId, setCognitoUserId] = useState(null);
  // MySQL users.id — what you use for folder paths, uploads, etc.
  const [mysqlUserId, setMysqlUserId] = useState(null);
  const [organization, setOrganization] = useState(null);

  // Bring Your Own Bucket: the buckets this user may view, and the one currently shown.
  const [buckets, setBuckets] = useState([{ id: "house", name: "My CloudGenomics storage", house: true }]);
  const [selectedBucket, setSelectedBucket] = useState("house");
  const [refreshKey, setRefreshKey] = useState(0);
  const isHouseBucket = selectedBucket === "house";

  const [existingFiles, setExistingFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [uploadMode, setUploadMode] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgressMap, setUploadProgressMap] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [downloading, setDownloading] = useState(false);
  const [existingProjects, setExistingProjects] = useState([]);
  const [selectedExistingProject, setSelectedExistingProject] = useState("");

  const navigate = useNavigate();
  const filesPerPage = 20;

  // Human-readable file size (bytes -> B/KB/MB/GB/TB).
  const formatSize = (bytes) => {
    if (bytes == null || isNaN(bytes)) return "—";
    if (bytes === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };
  

  // ⭐ Fetch session: get BOTH Cognito sub and MySQL user id
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await fetch("/api/auth/session", {
          method: "GET",
          credentials: "include"
        });

        const data = await response.json();
        
        if (!data.authenticated) {
          console.error("User not authenticated");
          return;
        }

        // These keys must match what your backend returns
        // Example shape:
        // { authenticated: true, userId: 1, cognitoUserId: "4458-...", organization: "Cloudgenomics", ... }
        setMysqlUserId(data.userId);
        setCognitoUserId(data.cognitoUserId);
        setOrganization(data.organization);

      } catch (err) {
        console.error("Error fetching session:", err);
      }
    };

    fetchSession();
  }, []);

  // ⭐ Fetch the buckets this user may view (house + granted BYOB buckets).
  useEffect(() => {
    if (!cognitoUserId) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/my-buckets`, { method: "GET", credentials: "include" });
        const data = await res.json();
        if (Array.isArray(data.buckets) && data.buckets.length) setBuckets(data.buckets);
      } catch (err) {
        console.error("Error fetching buckets:", err);
      }
    })();
  }, [cognitoUserId]);

  // ⭐ Fetch user files for the selected bucket. Identity + authorization are enforced
  // server-side from the verified session; bucket_id selects which bucket to list.
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        if (!cognitoUserId) return;

        const res = await fetch(`${API_BASE}/user-files`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ bucket_id: selectedBucket })
        });

        const data = await res.json();

        const normalized = (data.files || []).map((file) => ({
          id: file.id,
          name: file.filename,
          project: file.original_project,
          path: file.s3_folder_path,
          size: file.size,
          createdRaw: file.created_on,
          created: new Date(file.created_on).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
        }));

        setExistingFiles(normalized);
        setSelectedFiles([]);
      } catch (err) {
        console.error("Error fetching user files:", err);
      }
    };

    fetchFiles();
  }, [cognitoUserId, selectedBucket, refreshKey]);

  // ⭐ NEW: derive existingProjects from existingFiles
  useEffect(() => {
    const projects = Array.from(
      new Set(existingFiles.map(f => f.project))
    ).filter(Boolean);

    setExistingProjects(projects);
  }, [existingFiles]);

  const sortedFiles = useMemo(() => {
    if (!sortConfig.key) return existingFiles;
    const { key, direction } = sortConfig;
    const dir = direction === "asc" ? 1 : -1;
    return [...existingFiles].sort((a, b) => {
      let aVal, bVal;
      if (key === "created") {
        // Sort chronologically using the raw timestamp, not the display string.
        aVal = new Date(a.createdRaw || 0).getTime();
        bVal = new Date(b.createdRaw || 0).getTime();
      } else if (key === "size") {
        // Sort numerically by raw byte count (missing sizes sort lowest).
        aVal = a.size == null ? -1 : a.size;
        bVal = b.size == null ? -1 : b.size;
      } else {
        aVal = (a[key] || "").toString().toLowerCase();
        bVal = (b[key] || "").toString().toLowerCase();
      }
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
  }, [existingFiles, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="userdata-sort-icon" />;
    return sortConfig.direction === "asc"
      ? <FaSortUp className="userdata-sort-icon active" />
      : <FaSortDown className="userdata-sort-icon active" />;
  };

  const filteredFiles = searchQuery.trim()
    ? sortedFiles.filter(
        (file) =>
          file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          file.project.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sortedFiles;

  const paginatedFiles = filteredFiles.slice(
    (currentPage - 1) * filesPerPage,
    currentPage * filesPerPage
  );

  // Select-all applies to every file in the current (filtered) result set, not
  // just the visible page, so a single click selects across pagination.
  const allFilteredSelected =
    filteredFiles.length > 0 && filteredFiles.every((f) => selectedFiles.includes(f.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedFiles((prev) => prev.filter((id) => !filteredFiles.some((f) => f.id === id)));
    } else {
      setSelectedFiles((prev) =>
        Array.from(new Set([...prev, ...filteredFiles.map((f) => f.id)]))
      );
    }
  };

  const toggleRow = (id) => {
    setSelectedFiles((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDownloadFiles = async () => {
    if (selectedFiles.length === 0) {
      alert("Please select files to download.");
      return;
    }

    try {
      setDownloading(true);
      const res = await fetch(`${API_BASE}/download-user-files`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ fileIds: selectedFiles }),
      });

      if (!res.ok) throw new Error(`Request failed (${res.status})`);

      const data = await res.json();
      const files = data.files || [];
      if (files.length === 0) {
        alert("No downloadable files found.");
        return;
      }

      // Trigger each presigned download sequentially; the small delay keeps the
      // browser from cancelling rapid successive downloads.
      for (const f of files) {
        const a = document.createElement("a");
        a.href = f.url;
        a.download = f.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        await new Promise((r) => setTimeout(r, 400));
      }
    } catch (err) {
      console.error("Failed to download files:", err);
      alert("Error downloading files.");
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteFiles = async () => {
    if (selectedFiles.length === 0) {
      alert("Please select files to delete.");
      return;
    }

    try {
      await fetch(`${API_BASE}/delete-user-files`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileIds: selectedFiles }),
      });

      setExistingFiles((prev) => prev.filter((f) => !selectedFiles.includes(f.id)));
      setSelectedFiles([]);
      alert("Selected files deleted successfully.");
    } catch (err) {
      console.error("Failed to delete files:", err);
      alert("Error deleting files.");
    }
  };

    const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      const newFiles = acceptedFiles.map((file) => ({
        file,
        progress: 0,
        status: "pending",
        id: uuidv4(),
      }));
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    },
    multiple: true,
  });

  const uploadFileWithMetadata = async (
    fileObj,
    folderPath,
    projectName,
    cognitoUserId,
    onProgress
  ) => {
    const file = fileObj.file;
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("cognitoUserId", cognitoUserId);
      formData.append("original_project", projectName);
      formData.append("folderPath", folderPath);
      formData.append("bucket_id", selectedBucket); // house or a granted BYOB bucket
      await axios.post(`${API_BASE}/nfcore-upload`, formData, {
        withCredentials: true,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        },
      });

      onProgress(100);
    } catch (err) {
      console.error(`Failed to upload ${file.name}:`, err);
      onProgress(-1);
    }
  };

  const handleUploadData = async () => {
    if (!projectName && !selectedExistingProject) {
      alert("Please enter a new project name OR select an existing project.");
      return;
    }

    if (projectName && selectedExistingProject) {
      alert("Please choose either a new project OR an existing project, not both.");
      return;
    }

    // Validate new project name
    if (projectName) {
      if (!/^[A-Za-z0-9_]+$/.test(projectName)) {
        alert("Project name may only contain letters, numbers, and underscores.");
        return;
      }

      if (existingProjects.includes(projectName)) {
        alert("A project with this name already exists.");
        return;
      }
    }

    // Final chosen project
    const finalProject = projectName || selectedExistingProject;
    if (!cognitoUserId) {
      alert("User session not ready yet. Please wait a moment and try again.");
      return;
    }

    // New layout: user uploads live under the user's DataUpload/ folder.
    const folderPath = `${cognitoUserId}/DataUpload/${finalProject}`;
    setUploading(true);

    try {
      await Promise.all(
        uploadedFiles.map((fileObj) =>
          uploadFileWithMetadata(
            fileObj,
            folderPath,
            finalProject,
            cognitoUserId,
            (percent) => {
              setUploadProgressMap((prev) => ({
                ...prev,
                [fileObj.file.name]: percent,
              }));
            }
          )
        )
      );

      alert("All files uploaded successfully.");
      setUploadedFiles([]);
      setProjectName("");
      setUploadMode(false);
      setRefreshKey((k) => k + 1); // re-list so the new file(s) appear (house or BYOB)
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

 return (
  <div className="userdata-container">
    <h1>Your Uploaded Data</h1>

    {buckets.length > 1 && (
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <label style={{ fontWeight: 600, fontSize: 14 }}>Bucket:</label>
        <select
          value={selectedBucket}
          onChange={(e) => { setSelectedBucket(e.target.value); setCurrentPage(1); }}
          style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14, minWidth: 260 }}
        >
          {buckets.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        {!isHouseBucket && (
          <span style={{ fontSize: 12, color: "#64748b" }}>
            Showing data from your organization's bucket (read-only here; use it as input when launching a pipeline).
          </span>
        )}
      </div>
    )}

    <input
      type="text"
      placeholder="Search by filename or project"
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      className="userdata-search"
    />

    <table className="userdata-table">
      <thead>
        <tr>
          <th className="userdata-th-select">
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={toggleSelectAll}
              title="Select all"
            />
          </th>
          <th className="userdata-th-sort" onClick={() => requestSort("name")}>
            Filename {renderSortIcon("name")}
          </th>
          <th className="userdata-th-sort" onClick={() => requestSort("project")}>
            Project {renderSortIcon("project")}
          </th>
          <th className="userdata-th-sort" onClick={() => requestSort("size")}>
            Size {renderSortIcon("size")}
          </th>
          <th className="userdata-th-sort" onClick={() => requestSort("created")}>
            Created On {renderSortIcon("created")}
          </th>
        </tr>
      </thead>
      <tbody>
        {paginatedFiles.length === 0 ? (
          <tr>
            <td colSpan={5} className="userdata-empty">No files found.</td>
          </tr>
        ) : (
          paginatedFiles.map((f) => (
            <tr key={f.id} className={selectedFiles.includes(f.id) ? "selected" : ""}>
              <td className="userdata-td-select">
                <input
                  type="checkbox"
                  checked={selectedFiles.includes(f.id)}
                  onChange={() => toggleRow(f.id)}
                />
              </td>
              <td>{f.name}</td>
              <td>{f.project}</td>
              <td>{formatSize(f.size)}</td>
              <td>{f.created}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>

    <Pagination
      page={currentPage}
      totalPages={Math.ceil(filteredFiles.length / filesPerPage)}
      onChange={setCurrentPage}
    />

    <div className="userdata-actions">
      <RequirePermission permission="deleteData">
        <button
          onClick={handleDeleteFiles}
          disabled={selectedFiles.length === 0 || !isHouseBucket}
          title={!isHouseBucket ? "Deleting is only available for your CloudGenomics storage." : undefined}
          className="userdata-btn delete"
        >
          Delete Selected Files{selectedFiles.length ? ` (${selectedFiles.length})` : ""}
        </button>
      </RequirePermission>

      <RequirePermission permission="downloadData">
        <button
          onClick={handleDownloadFiles}
          disabled={selectedFiles.length === 0 || downloading || !isHouseBucket}
          title={!isHouseBucket ? "Download is only available for your CloudGenomics storage in this view." : undefined}
          className="userdata-btn download"
        >
          {downloading
            ? "Preparing download…"
            : `Download Selected Files${selectedFiles.length ? ` (${selectedFiles.length})` : ""}`}
        </button>
      </RequirePermission>

      {!uploadMode ? (
        <>
          <button
            onClick={() => setUploadMode(true)}
            className="userdata-btn upload"
          >
            Upload New Files
          </button>

          {/* ⭐ NEW BUTTON */}
          <button
            onClick={() => navigate("/public-data")}
            className="userdata-btn sra-download"
          >
            Download Public Data
          </button>

          <button
            onClick={() => navigate("/downloads")}
            className="userdata-btn sra-download"
          >
            Previous Downloads
          </button>

        </>
      ) : (
        <button
          onClick={() => setUploadMode(false)}
          className="userdata-btn cancel-upload"
        >
          Cancel Upload
        </button>
      )}
    </div>

    {uploadMode && (
      <div className="userdata-upload-section">
        <div className="userdata-project-row">

          {/* Left column — New Project */}
          <div className="userdata-project-col">
            <label>Enter New Project Name</label>
            <input
              type="text"
              className="userdata-project-input"
              value={projectName}
              onChange={(e) => {
                setProjectName(e.target.value);
                setSelectedExistingProject(""); // prevent selecting both
              }}
              placeholder="New project name"
            />
          </div>

          {/* Middle divider */}
          <div className="userdata-project-or">OR</div>

          {/* Right column — Existing Project */}
          <div className="userdata-project-col">
            <label>Select Existing Project</label>
            <SearchableSelect
              value={selectedExistingProject}
              options={existingProjects}
              placeholder="-- Select an existing project --"
              searchPlaceholder="Search projects…"
              onChange={(v) => {
                setSelectedExistingProject(v);
                if (v) setProjectName(""); // prevent selecting both
              }}
            />
          </div>

        </div>


        <div {...getRootProps()} className="userdata-dropzone">
          <input {...getInputProps()} />
          <p>Drag & drop files here, or click to select</p>

          <div className="userdata-upload-grid">
            {uploadedFiles.map((fileObj) => {
              const progress = uploadProgressMap[fileObj.file.name] ?? 0;
              const isError = progress < 0;

              return (
                <div key={fileObj.id} className="userdata-upload-card">
                  <strong>{fileObj.file.name}</strong>

                  <div className="userdata-progress-bar">
                    <div
                      className={`userdata-progress-fill ${isError ? "error" : ""}`}
                      style={{ width: `${Math.max(0, progress)}%` }}
                    />
                  </div>

                  <div className={`userdata-progress-text ${isError ? "error" : ""}`}>
                    {isError ? "Upload failed" : `${progress}%`}
                  </div>

                  <button
                    onClick={() =>
                      setUploadedFiles((prev) =>
                        prev.filter((f) => f.id !== fileObj.id)
                      )
                    }
                    className="userdata-remove-file"
                  >
                    Remove File
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <button
          onClick={handleUploadData}
          disabled={uploading || uploadedFiles.length === 0}
          className="userdata-btn start-upload"
        >
          {uploading ? "Uploading... (Do not navigate away)" : "Start Upload"}
        </button>
      </div>
    )}
  </div>
);
};
export default UserData;